import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditService } from '../services/auditService'
import type { AuditEntry, AuditHistoryResponse, AuditQueryParams } from '../types/audit'
import { formatDate } from '../utils/locale'
import './PatientAuditHistory.css'

interface PatientAuditHistoryProps {
  patientId: string
  initialHistory?: AuditHistoryResponse
}

export function PatientAuditHistory({ patientId, initialHistory }: PatientAuditHistoryProps) {
  const [filters, setFilters] = useState<AuditQueryParams>({
    limit: 100,
    changeType: undefined,
    startTime: undefined,
    endTime: undefined,
  })

  const hasInitialHistory = initialHistory !== undefined
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['patient-audit', patientId, filters],
    queryFn: () => auditService.getPatientAuditHistory(patientId, filters),
    enabled: !!patientId && !hasInitialHistory,
    initialData: initialHistory,
  })

  const handleExport = () => {
    if (!data?.entries) return

    const csv = auditService.exportAuditHistoryAsCsv(data.entries)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `patient-audit-${patientId}-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'create':
        return 'var(--color-success)'
      case 'update':
        return 'var(--color-info)'
      case 'archive':
        return 'var(--color-warning)'
      case 'restore':
        return 'var(--color-success)'
      case 'view':
        return 'var(--color-muted)'
      case 'delete':
        return 'var(--color-danger)'
      default:
        return 'var(--color-text)'
    }
  }

  /** Normalize changes to an array; some Lambdas store changes as object { fieldName: { from, to } } */
  const getChangesArray = (entry: AuditEntry): AuditEntry['changes'] => {
    const c = entry.changes
    if (c == null) return []
    if (Array.isArray(c)) {
      return c.map((ch) => ({
        field: ch.field ?? (ch as any).fieldName ?? '',
        previousValue: (ch as any).previousValue ?? (ch as any).before ?? (ch as any).from,
        newValue: (ch as any).newValue ?? (ch as any).after ?? (ch as any).to,
      }))
    }
    if (typeof c === 'object' && !Array.isArray(c)) {
      return Object.entries(c).map(([field, v]) => {
        const val = v as Record<string, unknown>
        return {
          field,
          previousValue: val.previousValue ?? val.before ?? val.from,
          newValue: val.newValue ?? val.after ?? val.to,
        }
      })
    }
    return []
  }

  const isEffectivelyEmpty = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return true
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
  }

  const formatChangeValue = (value: any): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  if (isLoading) {
    return (
      <div className="patient-audit-history">
        <div className="loading">Loading audit history...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="patient-audit-history">
        <div className="error">
          Error loading audit history: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    )
  }

  const entries = data?.entries || []

  return (
    <div className="patient-audit-history">
      <div className="patient-audit-history-header">
        <h2>Audit History</h2>
        <div className="patient-audit-history-actions">
          <button
            type="button"
            onClick={() => refetch()}
            className="btn btn-secondary patient-audit-history-refresh"
            aria-label="Refresh audit history"
            title="Refresh audit history"
            disabled={isFetching}
          >
            ↻
          </button>
          <button onClick={handleExport} className="btn btn-secondary" disabled={entries.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="patient-audit-history-filters">
        <select
          value={filters.changeType || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              changeType: e.target.value ? (e.target.value as AuditEntry['changeType']) : undefined,
            })
          }
          className="form-control"
        >
          <option value="">All Change Types</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="archive">Archive</option>
          <option value="restore">Restore</option>
          <option value="view">View</option>
        </select>

        <input
          type="date"
          value={filters.startTime?.split('T')[0] || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              startTime: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
            })
          }
          className="form-control"
          placeholder="Start Date"
        />

        <input
          type="date"
          value={filters.endTime?.split('T')[0] || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              endTime: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
            })
          }
          className="form-control"
          placeholder="End Date"
        />
      </div>

      {entries.length === 0 ? (
        <div className="patient-audit-history-empty">
          <p>No audit history found for this patient.</p>
        </div>
      ) : (
        <div className="patient-audit-history-list">
          {entries.map((entry) => (
            <div key={entry.id} className="patient-audit-history-entry">
              <div className="patient-audit-history-entry-header">
                <div className="patient-audit-history-entry-meta">
                  <span
                    className="patient-audit-history-change-type"
                    style={{ color: getChangeTypeColor(entry.changeType || 'unknown') }}
                  >
                    {(entry.changeType || 'unknown').toUpperCase()}
                  </span>
                  <span className="patient-audit-history-timestamp">
                    {formatDate(entry.timestamp, 'dd/MM/yyyy HH:mm')}
                  </span>
                  <span className="patient-audit-history-user">
                    {entry.performedByDisplayName || entry.performedBy}
                  </span>
                  {entry.reason && (
                    <span className="patient-audit-history-reason">{entry.reason}</span>
                  )}
                </div>
                {entry.metadata.ipAddress && (
                  <span className="patient-audit-history-ip">{entry.metadata.ipAddress}</span>
                )}
              </div>

              {getChangesArray(entry)
                .filter(
                  (change) =>
                    !(isEffectivelyEmpty(change.previousValue) && isEffectivelyEmpty(change.newValue)),
                ).length > 0 && (
                <div className="patient-audit-history-changes">
                  <h4>Changes:</h4>
                  <table className="patient-audit-history-changes-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Previous Value</th>
                        <th>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getChangesArray(entry)
                        .filter(
                          (change) =>
                            !(
                              isEffectivelyEmpty(change.previousValue) &&
                              isEffectivelyEmpty(change.newValue)
                            ),
                        )
                        .map((change, idx) => (
                          <tr key={idx}>
                            <td className="patient-audit-history-field">{change.field}</td>
                            <td className="patient-audit-history-old-value">
                              <pre>{formatChangeValue(change.previousValue)}</pre>
                            </td>
                            <td className="patient-audit-history-new-value">
                              <pre>{formatChangeValue(change.newValue)}</pre>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {getChangesArray(entry).filter(
                (change) =>
                  !(isEffectivelyEmpty(change.previousValue) && isEffectivelyEmpty(change.newValue)),
              ).length === 0 && (
                <div className="patient-audit-history-no-changes">
                  <p>No field-level changes recorded.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="patient-audit-history-footer">
          <p>Showing {entries.length} audit entries</p>
        </div>
      )}
    </div>
  )
}
